import React from 'react';
import { Chipset, FieldedChip } from '../../components/types/stylepacks';
import * as con from '../../connection/content-chips';
import { GreenroomState } from '../../stores/types/types';
import { useSelector, useDispatch } from 'react-redux';
import { ClearChipset, LoadChipset, LoadChipsetDraft, SetChipChannel } from '../../stores/actions/content-chips';
import { useDragDetectFeed } from '../../hooks/content/use-drag-detect-feed';
import { regenerateContent } from '../../connection/generate-content';
import { GetChipset, UpsertContentChipsDraft } from '../../connection/content-chips';

type ChipMap = Map<string, FieldedChip>

type TypeChipContext = {
	chipsetUserId: number | null,
	chipMap: ChipMap,
	attachChip: (dataKey: string, chipType: string) => void
	attachUser: (userId: number | null) => void
	clearChip: (dataKey: string) => void
	shouldShowChipMenu: (forceShow?: boolean) => boolean
};


export const ChipContext = React.createContext<TypeChipContext>({
	chipsetUserId: null,
	chipMap: new Map(),
	attachChip: () => { },
	attachUser: () => { },
	clearChip: () => { },
	shouldShowChipMenu: () => false
});

type Props = {
	children: React.ReactNode
	channelId: number;
	contentId?: number;
	uniqueId: string;
	forceShow?: boolean; // titles always show chip dropdown
	onChange: (e?: any, initialChipUser?: number) => void;
	isForShotEditorModal?: boolean;
}

const parseMediaId = (str: string) => parseInt(str.split(':')[1] ?? '0', 10);

export default function ChipProvider(props: Props) {
	const dispatch = useDispatch();
	const {
		chipMap,
		chipsetUser,
		hangingUserIds,
		loadingChip,
		draftChipset
	} = useSelector((s:GreenroomState) => s.ContentChipsReducer);
	const room = useSelector((s:GreenroomState) => s.SessionReducer.meeting?.meeting ?? 0);
	const {channelId, contentId, uniqueId, forceShow, isForShotEditorModal} = props;

	const {feedPresenter} = useDragDetectFeed(parseMediaId(uniqueId), isForShotEditorModal)

	React.useEffect(() => {
		dispatch(SetChipChannel(channelId));
	}, [channelId])

	React.useEffect(() => {
		dispatch(ClearChipset(null));
		return () => {
			dispatch(ClearChipset(null));
		}
	}, [uniqueId]);

	React.useEffect(() => {
		if (contentId) {
			dispatch(isForShotEditorModal
				? LoadChipsetDraft(room, contentId)
				: LoadChipset(channelId, contentId)
			);
		} else if (feedPresenter) {
			// no content id and over a presenter feed, generate new item
			props.onChange(undefined, feedPresenter);
		}
	}, [contentId, feedPresenter]);

	const attachUser = React.useCallback(async (userId: number | null) => {
		if (userId === chipsetUser) {
			return;
		}

		if (!contentId) {
			// no content id and selected a presenter, generate new item
			if (userId)
				props.onChange(undefined, userId);
			return
		}

		if (userId) {
			if (isForShotEditorModal) {
				const chipsetDraftEntity = await con.GetContentChipsDraft(room, contentId);
				const chipset = chipsetDraftEntity?.entityValue as Chipset;
				if (chipset &&chipset?.gr_user !== userId) {
					await UpsertContentChipsDraft(room, contentId, { ...chipset, gr_user: userId });
					regenerateContent(room, contentId).catch(() => false);
				}
			} else {
				const chipset = await GetChipset(channelId, contentId)
				if (chipset?.gr_user !== userId) {
					await con.SetChipset(channelId, contentId, userId);
					regenerateContent(room, contentId).catch(() => false);
				}
			}
		} else {
			if (isForShotEditorModal) {
				if (draftChipset) {
					await UpsertContentChipsDraft(room, contentId, { ...draftChipset, gr_user: 0, chips: [] });
				}
			} else {
				await con.DeleteChipset(channelId, contentId);
			}
		}

		if (contentId) {
			dispatch(isForShotEditorModal
				? LoadChipsetDraft(room, contentId)
				: LoadChipset(channelId, contentId)
			);
		}
	}, [channelId, contentId, uniqueId, chipsetUser, room, isForShotEditorModal]);

	React.useEffect(() => {
		// a loaded chip id of 0 means the load succeeded but no user
		// is attached. otherwise loadedChip is null if not loaded
		if (loadingChip) return;

		if (feedPresenter !== null && feedPresenter !== chipsetUser) {
			// if this changes, the graphic was dragged over another feed
			// and we need to attach the new user id from that feed
			// console.log(`Chipset user changed from ${chipsetUser} to ${feedPresenter} - attaching...`)
			attachUser(feedPresenter);
		}
	}, [loadingChip, chipsetUser, feedPresenter, attachUser])

	const attachChip = React.useCallback(async (dataKey: string, chipType: string) => {
		if (!contentId) return console.warn('tried to attach chip with no content');
		if (isForShotEditorModal) {
			await UpsertContentChipsDraft(
				room,
				contentId,
				{
					...draftChipset,
					chips: [...(draftChipset?.chips ?? []), { data_key: dataKey, chip_type: chipType }]
				} as Chipset
			);
		} else {
			await con.SetChip(channelId, contentId, dataKey, chipType);
			dispatch(LoadChipset(channelId, contentId));
		}
	}, [channelId, contentId, draftChipset])
	
	const clearChip = React.useCallback(async (dataKey: string) => {
		if (!contentId) return console.warn('tried to clear chip with no content');
		if (isForShotEditorModal) {
			await UpsertContentChipsDraft(
				room,
				contentId,
				{
					...draftChipset,
					chips: draftChipset?.chips?.filter((chip) => chip.data_key !== dataKey)
				} as Chipset
			);
		} else {
			await con.DeleteChip(channelId, contentId, dataKey);
			dispatch(LoadChipset(channelId, contentId));
		}
	}, [channelId, contentId, draftChipset, isForShotEditorModal])

	const chipsetUserId = React.useMemo(() => {
		if (chipsetUser) return chipsetUser;
		return hangingUserIds.get(uniqueId) ?? null;
	}, [chipsetUser, uniqueId])

	const shouldShowChipMenu = React.useCallback((showForPresenterInfo?: boolean) => {
		// titles always show chip dropdown
		if (forceShow) return true;
		// presenter info on walkups/other templates show chip selection dropdown
		if (showForPresenterInfo) return true;
		return false;
	}, [forceShow])

	return <ChipContext.Provider value={{
		chipsetUserId,
		chipMap,
		attachUser,
		attachChip,
		clearChip,
		shouldShowChipMenu
	}}>
		{props.children}
	</ChipContext.Provider>;
}